import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import {useQuery, useMutation} from '@apollo/client';
import {GET_DOCUMENT_GROUPS} from '../../graphql/queries';
import {ADD_SOURCE_ITEMS_TO_GROUP} from '../../graphql/mutations';
import {useOrg} from '../../contexts/OrgContext';
import {colors} from '../../theme/colors';

interface AddToGroupDialogProps {
  open: boolean;
  onClose: () => void;
  sourceItemIds: string[];
}

export default function AddToGroupDialog({open, onClose, sourceItemIds}: AddToGroupDialogProps) {
  const {activeOrg} = useOrg();
  const [snackbar, setSnackbar] = React.useState<{open: boolean; message: string; severity: 'success' | 'error'}>({
    open: false, message: '', severity: 'success',
  });

  const {data, loading, error} = useQuery(GET_DOCUMENT_GROUPS, {
    variables: {orgId: activeOrg?.id},
    skip: !activeOrg?.id,
  });

  const [addToGroup, {loading: adding}] = useMutation(ADD_SOURCE_ITEMS_TO_GROUP);

  const handleSelectGroup = async (groupId: string) => {
    try {
      await addToGroup({
        variables: {
          groupId,
          sourceItemIds,
          assignedBy: 'admin',
        },
      });
      setSnackbar({open: true, message: `Added ${sourceItemIds.length} items to group`, severity: 'success'});
      setTimeout(onClose, 1000);
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Failed to add to group', severity: 'error'});
    }
  };

  const groups = data?.documentGroups || [];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{fontWeight: 600}}>Add to document group</DialogTitle>
        <DialogContent>
          {loading && (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{mb: 2}}>
              Failed to load groups
            </Alert>
          )}

          {!loading && groups.length === 0 && (
            <Typography sx={{fontSize: 14, color: '#6B7280', py: 2, textAlign: 'center'}}>
              No document groups yet. Create one from the Document Groups page.
            </Typography>
          )}

          {groups.map((group: any) => (
            <Box
              key={group.id}
              onClick={adding ? undefined : () => handleSelectGroup(group.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: '8px',
                cursor: adding ? 'default' : 'pointer',
                opacity: adding ? 0.6 : 1,
                '&:hover': adding ? {} : {bgcolor: colors.gray[50]},
              }}
            >
              <FolderIcon sx={{color: colors.blue[500], fontSize: 22}} />
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography sx={{fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                  {group.name}
                </Typography>
                <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>
                  {group.members?.length || 0} sources
                </Typography>
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity} sx={{width: '100%'}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
